import React from "react";
import { useLanguage } from "@/App";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const CookieBeleid = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      <div className="pt-32 pb-24 max-w-4xl mx-auto px-6 md:px-12">
        <a href="/" className="inline-flex items-center gap-2 text-[#FF6B00] text-sm font-bold mb-8 hover:underline">
          <ArrowLeft size={16} />
          {language === "nl" ? "Terug naar home" : "Back to home"}
        </a>

        <h1 className="text-4xl sm:text-5xl font-black text-white mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {language === "nl" ? "Cookiebeleid" : "Cookie Policy"}
        </h1>

        <div className="prose prose-invert prose-zinc max-w-none space-y-8 text-zinc-300 leading-relaxed">
          {language === "nl" ? (
            <>
              <p className="text-zinc-400 text-sm">Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}</p>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Wat zijn cookies?</h2>
                <p>Cookies zijn kleine tekstbestanden die door websites op uw computer of mobiel apparaat worden geplaatst. Ze worden veel gebruikt om websites efficiënt te laten werken en om informatie te verstrekken aan de eigenaren van de website.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Welke cookies gebruiken wij?</h2>

                <h3 className="text-lg font-bold text-white mt-6 mb-3">Noodzakelijke cookies</h3>
                <p>Deze cookies zijn essentieel voor het functioneren van de website. Zonder deze cookies kan de website niet naar behoren functioneren.</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Taalvoorkeur (NL/EN)</li>
                  <li>Cookie-toestemming opslag</li>
                </ul>

                <h3 className="text-lg font-bold text-white mt-6 mb-3">Analytische cookies</h3>
                <p>Wij gebruiken PostHog om te begrijpen hoe bezoekers onze website gebruiken. Deze gegevens helpen ons de website te verbeteren.</p>

                <h3 className="text-lg font-bold text-white mt-6 mb-3">Functionele cookies</h3>
                <p>Tawk.to plaatst cookies voor de live chat functionaliteit, zodat gesprekken kunnen worden voortgezet bij herbezoek.</p>

                <h3 className="text-lg font-bold text-white mt-6 mb-3">Betalingscookies</h3>
                <p>Stripe plaatst cookies die noodzakelijk zijn voor het veilig verwerken van betalingen.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Cookies beheren</h2>
                <p>U kunt cookies beheren en verwijderen via uw browserinstellingen. Let op: het uitschakelen van cookies kan de functionaliteit van deze website beperken.</p>
                <p>De meeste browsers bieden de mogelijkheid om:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Alle cookies te bekijken en te verwijderen</li>
                  <li>Cookies van derden te blokkeren</li>
                  <li>Alle cookies te blokkeren</li>
                  <li>Alle cookies te verwijderen bij het sluiten van de browser</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Toestemming</h2>
                <p>Bij uw eerste bezoek aan onze website vragen wij uw toestemming voor het plaatsen van cookies. U kunt uw toestemming te allen tijde intrekken door uw cookies te verwijderen via uw browser.</p>
              </section>

              <section className="border-t border-white/10 pt-8 mt-12">
                <p>Vragen over ons cookiebeleid? Neem contact op via ons contactformulier of WhatsApp: +31 6 84006668</p>
              </section>
            </>
          ) : (
            <>
              <p className="text-zinc-400 text-sm">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>What are cookies?</h2>
                <p>Cookies are small text files placed on your device by websites. They are widely used to make websites work efficiently and provide information to website owners.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Which cookies do we use?</h2>
                <h3 className="text-lg font-bold text-white mt-6 mb-3">Essential cookies</h3>
                <p>Required for the website to function. Includes language preference and cookie consent storage.</p>
                <h3 className="text-lg font-bold text-white mt-6 mb-3">Analytics cookies</h3>
                <p>PostHog helps us understand how visitors use our website.</p>
                <h3 className="text-lg font-bold text-white mt-6 mb-3">Functional cookies</h3>
                <p>Tawk.to uses cookies for live chat functionality.</p>
                <h3 className="text-lg font-bold text-white mt-6 mb-3">Payment cookies</h3>
                <p>Stripe uses cookies necessary for secure payment processing.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Managing cookies</h2>
                <p>You can manage and delete cookies through your browser settings. Note: disabling cookies may limit website functionality.</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-white mt-8 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Consent</h2>
                <p>On your first visit, we ask your consent for placing cookies. You may withdraw consent at any time by deleting cookies via your browser.</p>
              </section>

              <section className="border-t border-white/10 pt-8 mt-12">
                <p>Questions about our cookie policy? Contact us via our contact form or WhatsApp: +31 6 84006668</p>
              </section>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CookieBeleid;
